import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import type { ApiError, AuthResponse } from '../../api/types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

interface TokenStore {
  accessToken: string | null;
  refreshToken: string | null;
}

const tokenStore: TokenStore = {
  accessToken: null,
  refreshToken: null,
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Object.prototype.toString.call(value) === '[object Object]';
}

function toSnakeCaseKey(key: string): string {
  return key.replace(/([a-z0-9])([A-Z])/g, '$1_$2').replace(/-/g, '_').toLowerCase();
}

function toCamelCaseKey(key: string): string {
  return key.replace(/_([a-z])/g, (_, char: string) => char.toUpperCase());
}

function transformKeysDeep<T>(value: T, transformKey: (key: string) => string): T {
  const isBlob = typeof Blob !== 'undefined' && value instanceof Blob;
  const isFile = typeof File !== 'undefined' && value instanceof File;
  const isFormData = typeof FormData !== 'undefined' && value instanceof FormData;
  const isSearchParams =
    typeof URLSearchParams !== 'undefined' && value instanceof URLSearchParams;

  if (
    value === null ||
    value === undefined ||
    value instanceof Date ||
    isBlob ||
    isFile ||
    isFormData ||
    isSearchParams
  ) {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => transformKeysDeep(item, transformKey)) as T;
  }

  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, entryValue]) => entryValue !== undefined)
        .map(([entryKey, entryValue]) => [
          transformKey(entryKey),
          transformKeysDeep(entryValue, transformKey),
        ]),
    ) as T;
  }

  return value;
}

function normalizeRequestData<T>(value: T): T {
  return transformKeysDeep(value, toSnakeCaseKey);
}

function normalizeResponseData<T>(value: T): T {
  return transformKeysDeep(value, toCamelCaseKey);
}

export function setTokens(accessToken: string, refreshToken: string): void {
  tokenStore.accessToken = accessToken;
  tokenStore.refreshToken = refreshToken;
}

export function clearTokens(): void {
  tokenStore.accessToken = null;
  tokenStore.refreshToken = null;
}

export function getAccessToken(): string | null {
  return tokenStore.accessToken;
}

export function getRefreshToken(): string | null {
  return tokenStore.refreshToken;
}

function createApiClient(): AxiosInstance {
  const client = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor: attach access token
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = tokenStore.accessToken;
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      if (config.params) {
        config.params = normalizeRequestData(config.params);
      }
      if (config.data) {
        config.data = normalizeRequestData(config.data);
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  // Response interceptor: handle 401 with token refresh
  client.interceptors.response.use(
    (response) => {
      response.data = normalizeResponseData(response.data);
      return response;
    },
    async (error: AxiosError<ApiError>) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      if (error.response?.data) {
        error.response.data = normalizeResponseData(error.response.data);
      }

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        const refreshToken = tokenStore.refreshToken;
        if (refreshToken) {
          try {
            // Use a bare axios call to avoid interceptor loop
            const refreshResponse = await axios.post<AuthResponse>(
              `${API_BASE_URL}/auth/refresh`,
              normalizeRequestData({ refreshToken }),
            );
            const normalizedRefresh = normalizeResponseData(refreshResponse.data);
            const { accessToken: newAccess, refreshToken: newRefresh } = normalizedRefresh;
            setTokens(newAccess, newRefresh);

            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newAccess}`;
            }
            return client(originalRequest);
          } catch {
            clearTokens();
            window.location.href = '/login';
            return Promise.reject(error);
          }
        } else {
          clearTokens();
          window.location.href = '/login';
        }
      }

      return Promise.reject(error);
    },
  );

  return client;
}

export const apiClient = createApiClient();

export function isApiError(error: unknown): error is AxiosError<ApiError> {
  return axios.isAxiosError(error) && !!error.response?.data;
}

export function getApiErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    return error.response?.data?.error?.message || error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}
